<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Register New Household</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Progress Steps -->
          <div class="mb-4">
            <div class="row justify-content-center">
              <div class="col-md-10">
                <div class="d-flex justify-content-between position-relative">
                  <!-- Progress Line -->
                  <div class="progress position-absolute w-100" style="top: 20px; height: 2px;">
                    <div class="progress-bar" :style="{width: progressPercentage + '%'}"></div>
                  </div>

                  <!-- Step 1 -->
                  <div class="text-center" style="z-index: 1;">
                    <div class="rounded-circle border d-inline-flex align-items-center justify-content-center"
                         :class="step >= 1 ? 'bg-primary text-white border-primary' : 'bg-white'"
                         style="width: 40px; height: 40px;">
                      <i class="bx" :class="step > 1 ? 'bx-check' : 'bx-home'"></i>
                    </div>
                    <p class="mt-2 mb-0 small">Basic Info</p>
                  </div>

                  <!-- Step 2 -->
                  <div class="text-center" style="z-index: 1;">
                    <div class="rounded-circle border d-inline-flex align-items-center justify-content-center"
                         :class="step >= 2 ? 'bg-primary text-white border-primary' : 'bg-white'"
                         style="width: 40px; height: 40px;">
                      <i class="bx" :class="step > 2 ? 'bx-check' : 'bx-map'"></i>
                    </div>
                    <p class="mt-2 mb-0 small">Location</p>
                  </div>

                  <!-- Step 3 -->
                  <div class="text-center" style="z-index: 1;">
                    <div class="rounded-circle border d-inline-flex align-items-center justify-content-center"
                         :class="step >= 3 ? 'bg-primary text-white border-primary' : 'bg-white'"
                         style="width: 40px; height: 40px;">
                      <i class="bx" :class="step > 3 ? 'bx-check' : 'bx-droplet'"></i>
                    </div>
                    <p class="mt-2 mb-0 small">WASH</p>
                  </div>

                  <!-- Step 4 -->
                  <div class="text-center" style="z-index: 1;">
                    <div class="rounded-circle border d-inline-flex align-items-center justify-content-center"
                         :class="step >= 4 ? 'bg-primary text-white border-primary' : 'bg-white'"
                         style="width: 40px; height: 40px;">
                      <i class="bx bx-check-double"></i>
                    </div>
                    <p class="mt-2 mb-0 small">Review</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 1: Basic Information -->
          <div v-if="step === 1">
            <h6 class="mb-3">Basic Household Information</h6>

            <div class="row">
              <!-- House Type -->
              <div class="col-md-6 mb-3">
                <label class="form-label">House Type <span class="text-danger">*</span></label>
                <select v-model="householdForm.houseType" class="form-select" required>
                  <option value="">Select house type</option>
                  <option value="tukul">Tukul (Traditional)</option>
                  <option value="corrugated_iron">Corrugated Iron</option>
                  <option value="concrete">Concrete/Block</option>
                  <option value="mixed">Mixed Materials</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <!-- Number of Rooms -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Number of Rooms</label>
                <input v-model.number="householdForm.numberOfRooms" type="number"
                       class="form-control" min="1" max="20">
              </div>

              <!-- Household Size Estimate -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Estimated Household Size</label>
                <input v-model.number="householdForm.estimatedSize" type="number"
                       class="form-control" min="1" max="30"
                       placeholder="Number of people living in household">
              </div>

              <!-- Economic Status -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Economic Status</label>
                <select v-model="householdForm.economicStatus" class="form-select">
                  <option value="">Select status</option>
                  <option value="very_poor">Very Poor</option>
                  <option value="poor">Poor</option>
                  <option value="middle">Middle Income</option>
                  <option value="better_off">Better Off</option>
                </select>
              </div>

              <!-- Special Categories -->
              <div class="col-12 mb-3">
                <label class="form-label">Special Categories</label>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-check">
                      <input v-model="householdForm.isFemaleHeaded" class="form-check-input"
                             type="checkbox" id="femaleHeadedCheck">
                      <label class="form-check-label" for="femaleHeadedCheck">
                        Female Headed Household
                      </label>
                    </div>
                    <div class="form-check">
                      <input v-model="householdForm.isVulnerable" class="form-check-input"
                             type="checkbox" id="vulnerableCheck">
                      <label class="form-check-label" for="vulnerableCheck">
                        Vulnerable Household
                      </label>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-check">
                      <input v-model="householdForm.hasElderlyMembers" class="form-check-input"
                             type="checkbox" id="elderlyCheck">
                      <label class="form-check-label" for="elderlyCheck">
                        Has Elderly Members (65+)
                      </label>
                    </div>
                    <div class="form-check">
                      <input v-model="householdForm.hasDisabledMembers" class="form-check-input"
                             type="checkbox" id="disabledCheck">
                      <label class="form-check-label" for="disabledCheck">
                        Has Disabled Members
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Notes -->
              <div class="col-12 mb-3">
                <label class="form-label">Additional Notes</label>
                <textarea v-model="householdForm.notes" class="form-control" rows="2"
                          placeholder="Any additional information about the household..."></textarea>
              </div>
            </div>
          </div>

          <!-- Step 2: Location Information -->
          <div v-else-if="step === 2">
            <h6 class="mb-3">Location Information</h6>

            <!-- Administrative Location -->
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Region <span class="text-danger">*</span></label>
                <select v-model="householdForm.regionId" @change="loadZones"
                        class="form-select" required>
                  <option value="">Select region</option>
                  <option v-for="region in regions" :key="region.region_id"
                          :value="region.region_id">
                    {{ region.region_name }}
                  </option>
                </select>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Zone <span class="text-danger">*</span></label>
                <select v-model="householdForm.zoneId" @change="loadWoredas"
                        class="form-select" required :disabled="!householdForm.regionId">
                  <option value="">Select zone</option>
                  <option v-for="zone in zones" :key="zone.zone_id"
                          :value="zone.zone_id">
                    {{ zone.zone_name }}
                  </option>
                </select>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Woreda <span class="text-danger">*</span></label>
                <select v-model="householdForm.woredaId" @change="loadKebeles"
                        class="form-select" required :disabled="!householdForm.zoneId">
                  <option value="">Select woreda</option>
                  <option v-for="woreda in woredas" :key="woreda.woreda_id"
                          :value="woreda.woreda_id">
                    {{ woreda.woreda_name }}
                  </option>
                </select>
              </div>

              <div class="col-md-6 mb-3">
                <label class="form-label">Kebele <span class="text-danger">*</span></label>
                <select v-model="householdForm.kebeleId" class="form-select"
                        required :disabled="!householdForm.woredaId">
                  <option value="">Select kebele</option>
                  <option v-for="kebele in kebeles" :key="kebele.kebele_id"
                          :value="kebele.kebele_id">
                    {{ kebele.kebele_name }}
                  </option>
                </select>
              </div>
            </div>

            <!-- GPS Coordinates -->
            <div class="row">
              <div class="col-12 mb-3">
                <div class="form-check">
                  <input v-model="captureGPS" class="form-check-input" type="checkbox"
                         id="gpsCheck">
                  <label class="form-check-label" for="gpsCheck">
                    Capture GPS Coordinates
                  </label>
                </div>
              </div>

              <div v-if="captureGPS" class="col-12">
                <div v-if="gpsLoading" class="alert alert-info">
                  <i class="bx bx-loader-alt bx-spin"></i> Getting GPS location...
                </div>
                <div v-else-if="gpsError" class="alert alert-danger">
                  <i class="bx bx-error-circle"></i> {{ gpsError }}
                  <button @click="retryGPS" class="btn btn-sm btn-danger float-end">Retry</button>
                </div>
                <div v-else-if="householdForm.gpsLatitude && householdForm.gpsLongitude"
                     class="alert alert-success">
                  <i class="bx bx-check-circle"></i> GPS coordinates captured successfully
                  <br>
                  <small>Lat: {{ householdForm.gpsLatitude }}, Long: {{ householdForm.gpsLongitude }}</small>
                </div>
              </div>
            </div>

            <!-- Physical Address -->
            <div class="row">
              <div class="col-12 mb-3">
                <label class="form-label">Physical Address/Landmark</label>
                <textarea v-model="householdForm.physicalAddress" class="form-control" rows="2"
                          placeholder="Describe location or nearby landmarks..."></textarea>
              </div>
            </div>
          </div>

          <!-- Step 3: WASH Information -->
          <div v-else-if="step === 3">
            <h6 class="mb-3">Water, Sanitation & Hygiene (WASH)</h6>

            <div class="row">
              <!-- Water Source -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Primary Water Source <span class="text-danger">*</span></label>
                <select v-model="householdForm.waterSource" class="form-select" required>
                  <option value="">Select water source</option>
                  <option value="piped_home">Piped Water to Home</option>
                  <option value="piped_yard">Piped Water to Yard</option>
                  <option value="public_tap">Public Tap/Standpipe</option>
                  <option value="protected_well">Protected Well</option>
                  <option value="unprotected_well">Unprotected Well</option>
                  <option value="protected_spring">Protected Spring</option>
                  <option value="unprotected_spring">Unprotected Spring</option>
                  <option value="river_stream">River/Stream</option>
                  <option value="rainwater">Rainwater Collection</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <!-- Water Treatment -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Water Treatment Method</label>
                <select v-model="householdForm.waterTreatment" class="form-select">
                  <option value="">No treatment</option>
                  <option value="boiling">Boiling</option>
                  <option value="chlorine">Chlorine/Water Guard</option>
                  <option value="filter">Water Filter</option>
                  <option value="solar">Solar Disinfection</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <!-- Latrine Type -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Latrine Type <span class="text-danger">*</span></label>
                <select v-model="householdForm.latrineType" class="form-select" required>
                  <option value="">Select latrine type</option>
                  <option value="none">No Latrine (Open Defecation)</option>
                  <option value="pit_with_slab">Pit Latrine with Slab</option>
                  <option value="pit_without_slab">Pit Latrine without Slab</option>
                  <option value="vip">VIP Latrine</option>
                  <option value="flush_septic">Flush to Septic Tank</option>
                  <option value="flush_sewer">Flush to Sewer</option>
                  <option value="composting">Composting Toilet</option>
                  <option value="shared">Shared Latrine</option>
                </select>
              </div>

              <!-- Handwashing Facility -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Handwashing Facility</label>
                <select v-model="householdForm.handwashingFacility" class="form-select">
                  <option value="">Select option</option>
                  <option value="none">No Facility</option>
                  <option value="water_only">Water Only</option>
                  <option value="water_soap">Water and Soap</option>
                  <option value="water_ash">Water and Ash/Sand</option>
                  <option value="tippy_tap">Tippy Tap</option>
                </select>
              </div>

              <!-- Waste Disposal -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Solid Waste Disposal <span class="text-danger">*</span></label>
                <select v-model="householdForm.wasteDisposal" class="form-select" required>
                  <option value="">Select disposal method</option>
                  <option value="pit">Waste Pit</option>
                  <option value="burning">Burning</option>
                  <option value="composting">Composting</option>
                  <option value="collection">Municipal Collection</option>
                  <option value="open_dumping">Open Dumping</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <!-- Cooking Fuel -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Primary Cooking Fuel</label>
                <select v-model="householdForm.cookingFuel" class="form-select">
                  <option value="">Select fuel type</option>
                  <option value="electricity">Electricity</option>
                  <option value="gas">Gas/LPG</option>
                  <option value="biogas">Biogas</option>
                  <option value="kerosene">Kerosene</option>
                  <option value="charcoal">Charcoal</option>
                  <option value="firewood">Firewood</option>
                  <option value="crop_residue">Crop Residue</option>
                  <option value="animal_dung">Animal Dung</option>
                </select>
              </div>

              <!-- Kitchen Location -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Kitchen Location</label>
                <select v-model="householdForm.kitchenLocation" class="form-select">
                  <option value="">Select location</option>
                  <option value="separate_building">Separate Building</option>
                  <option value="separate_room">Separate Room</option>
                  <option value="inside_living">Inside Living Area</option>
                  <option value="outside">Outside</option>
                </select>
              </div>

              <!-- Electricity Access -->
              <div class="col-md-6 mb-3">
                <div class="form-check mt-4">
                  <input v-model="householdForm.electricityAccess" class="form-check-input"
                         type="checkbox" id="electricityCheck">
                  <label class="form-check-label" for="electricityCheck">
                    Has Electricity Access
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 4: Review -->
          <div v-else-if="step === 4">
            <h6 class="mb-3">Review Household Information</h6>

            <div class="row">
              <!-- Basic Info Summary -->
              <div class="col-md-6 mb-4">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Basic Information</h6>
                  </div>
                  <div class="card-body">
                    <dl class="row mb-0">
                      <dt class="col-6">House Type:</dt>
                      <dd class="col-6">{{ getDisplayValue('houseType', householdForm.houseType) }}</dd>

                      <dt class="col-6">Number of Rooms:</dt>
                      <dd class="col-6">{{ householdForm.numberOfRooms || 'Not specified' }}</dd>

                      <dt class="col-6">Estimated Size:</dt>
                      <dd class="col-6">{{ householdForm.estimatedSize || 'Not specified' }} people</dd>

                      <dt class="col-6">Economic Status:</dt>
                      <dd class="col-6">{{ getDisplayValue('economicStatus', householdForm.economicStatus) }}</dd>
                    </dl>

                    <div v-if="getSpecialCategories().length > 0" class="mt-3">
                      <strong>Special Categories:</strong>
                      <div>
                        <span v-for="category in getSpecialCategories()" :key="category"
                              class="badge bg-info me-1">{{ category }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Location Summary -->
              <div class="col-md-6 mb-4">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Location</h6>
                  </div>
                  <div class="card-body">
                    <p class="mb-2">
                      <strong>Administrative:</strong><br>
                      {{ getLocationSummary() }}
                    </p>

                    <p v-if="householdForm.gpsLatitude && householdForm.gpsLongitude" class="mb-2">
                      <strong>GPS:</strong><br>
                      Lat: {{ householdForm.gpsLatitude }}, Long: {{ householdForm.gpsLongitude }}
                    </p>

                    <p v-if="householdForm.physicalAddress" class="mb-0">
                      <strong>Physical Address:</strong><br>
                      {{ householdForm.physicalAddress }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- WASH Summary -->
              <div class="col-12 mb-4">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Water, Sanitation & Hygiene</h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-6">
                        <dl class="row mb-0">
                          <dt class="col-6">Water Source:</dt>
                          <dd class="col-6">{{ getDisplayValue('waterSource', householdForm.waterSource) }}</dd>

                          <dt class="col-6">Water Treatment:</dt>
                          <dd class="col-6">{{ getDisplayValue('waterTreatment', householdForm.waterTreatment) || 'None' }}</dd>

                          <dt class="col-6">Latrine Type:</dt>
                          <dd class="col-6">{{ getDisplayValue('latrineType', householdForm.latrineType) }}</dd>

                          <dt class="col-6">Handwashing:</dt>
                          <dd class="col-6">{{ getDisplayValue('handwashingFacility', householdForm.handwashingFacility) || 'Not specified' }}</dd>
                        </dl>
                      </div>
                      <div class="col-md-6">
                        <dl class="row mb-0">
                          <dt class="col-6">Waste Disposal:</dt>
                          <dd class="col-6">{{ getDisplayValue('wasteDisposal', householdForm.wasteDisposal) }}</dd>

                          <dt class="col-6">Cooking Fuel:</dt>
                          <dd class="col-6">{{ getDisplayValue('cookingFuel', householdForm.cookingFuel) || 'Not specified' }}</dd>

                          <dt class="col-6">Kitchen Location:</dt>
                          <dd class="col-6">{{ getDisplayValue('kitchenLocation', householdForm.kitchenLocation) || 'Not specified' }}</dd>

                          <dt class="col-6">Electricity:</dt>
                          <dd class="col-6">{{ householdForm.electricityAccess ? 'Yes' : 'No' }}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Model Household Eligibility -->
              <div class="col-12">
                <div class="alert" :class="isModelHouseholdEligible() ? 'alert-success' : 'alert-info'">
                  <h6 class="alert-heading">
                    <i class="bx" :class="isModelHouseholdEligible() ? 'bx-check-circle' : 'bx-info-circle'"></i>
                    Model Household Status
                  </h6>
                  <p class="mb-0">
                    This household meets {{ getModelCriteriaCount() }} out of 5 model household criteria.
                    <span v-if="isModelHouseholdEligible()">
                      <strong>Eligible for model household certification!</strong>
                    </span>
                    <span v-else>
                      Needs {{ 4 - getModelCriteriaCount() }} more criteria to qualify.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>

          <button v-if="step > 1" type="button" class="btn btn-secondary me-2"
                  @click="previousStep" :disabled="submitting">
            <i class="bx bx-chevron-left"></i> Previous
          </button>

          <button v-if="step < 4" type="button" class="btn btn-primary"
                  @click="nextStep" :disabled="!canProceed()">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <button v-else type="button" class="btn btn-success"
                  @click="registerHousehold" :disabled="submitting">
            <span v-if="submitting">
              <span class="spinner-border spinner-border-sm me-1"></span> Registering...
            </span>
            <span v-else>
              <i class="bx bx-check"></i> Register Household
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "RegisterHouseholdModal",
  data() {
    return {
      step: 1,
      loading: false,
      submitting: false,

      // Form data
      householdForm: {
        // Basic Info
        houseType: '',
        numberOfRooms: null,
        estimatedSize: null,
        economicStatus: '',
        isFemaleHeaded: false,
        isVulnerable: false,
        hasElderlyMembers: false,
        hasDisabledMembers: false,
        notes: '',

        // Location
        regionId: null,
        zoneId: null,
        woredaId: null,
        kebeleId: null,
        gpsLatitude: null,
        gpsLongitude: null,
        physicalAddress: '',

        // WASH
        waterSource: '',
        waterTreatment: '',
        latrineType: '',
        handwashingFacility: '',
        wasteDisposal: '',
        cookingFuel: '',
        kitchenLocation: '',
        electricityAccess: false
      },

      // Location dropdowns
      regions: [],
      zones: [],
      woredas: [],
      kebeles: [],

      // GPS
      captureGPS: false,
      gpsLoading: false,
      gpsError: null,

      // Display values mapping
      displayValues: {
        houseType: {
          'tukul': 'Tukul (Traditional)',
          'corrugated_iron': 'Corrugated Iron',
          'concrete': 'Concrete/Block',
          'mixed': 'Mixed Materials',
          'other': 'Other'
        },
        economicStatus: {
          'very_poor': 'Very Poor',
          'poor': 'Poor',
          'middle': 'Middle Income',
          'better_off': 'Better Off'
        },
        waterSource: {
          'piped_home': 'Piped Water to Home',
          'piped_yard': 'Piped Water to Yard',
          'public_tap': 'Public Tap/Standpipe',
          'protected_well': 'Protected Well',
          'unprotected_well': 'Unprotected Well',
          'protected_spring': 'Protected Spring',
          'unprotected_spring': 'Unprotected Spring',
          'river_stream': 'River/Stream',
          'rainwater': 'Rainwater Collection',
          'other': 'Other'
        },
        waterTreatment: {
          'boiling': 'Boiling',
          'chlorine': 'Chlorine/Water Guard',
          'filter': 'Water Filter',
          'solar': 'Solar Disinfection',
          'other': 'Other'
        },
        latrineType: {
          'none': 'No Latrine (Open Defecation)',
          'pit_with_slab': 'Pit Latrine with Slab',
          'pit_without_slab': 'Pit Latrine without Slab',
          'vip': 'VIP Latrine',
          'flush_septic': 'Flush to Septic Tank',
          'flush_sewer': 'Flush to Sewer',
          'composting': 'Composting Toilet',
          'shared': 'Shared Latrine'
        },
        handwashingFacility: {
          'none': 'No Facility',
          'water_only': 'Water Only',
          'water_soap': 'Water and Soap',
          'water_ash': 'Water and Ash/Sand',
          'tippy_tap': 'Tippy Tap'
        },
        wasteDisposal: {
          'pit': 'Waste Pit',
          'burning': 'Burning',
          'composting': 'Composting',
          'collection': 'Municipal Collection',
          'open_dumping': 'Open Dumping',
          'other': 'Other'
        },
        cookingFuel: {
          'electricity': 'Electricity',
          'gas': 'Gas/LPG',
          'biogas': 'Biogas',
          'kerosene': 'Kerosene',
          'charcoal': 'Charcoal',
          'firewood': 'Firewood',
          'crop_residue': 'Crop Residue',
          'animal_dung': 'Animal Dung'
        },
        kitchenLocation: {
          'separate_building': 'Separate Building',
          'separate_room': 'Separate Room',
          'inside_living': 'Inside Living Area',
          'outside': 'Outside'
        }
      }
    };
  },

  computed: {
    progressPercentage() {
      return ((this.step - 1) / 3) * 100;
    }
  },

  watch: {
    captureGPS(value) {
      if (value) {
        this.getGPSLocation();
      } else {
        this.householdForm.gpsLatitude = null;
        this.householdForm.gpsLongitude = null;
        this.gpsError = null;
      }
    }
  },

  mounted() {
    this.loadRegions();

    // Pre-select HEW's kebele if available
    const userKebeleId = sessionStorage.getItem('userKebeleId');
    if (userKebeleId) {
      this.preselectLocation(userKebeleId);
    }
  },

  methods: {
    async loadRegions() {
      try {
        const res = await executeJson({ function: "getRegions" }, authUrl);
        if (res.data.success) {
          this.regions = res.data.data.regions || [];
        }
      } catch (e) {
        console.error("Failed to load regions:", e);
      }
    },

    async loadZones() {
      if (!this.householdForm.regionId) {
        this.zones = [];
        this.householdForm.zoneId = null;
        return;
      }

      try {
        const res = await executeJson({
          function: "getZones",
          regionId: this.householdForm.regionId
        }, authUrl);

        if (res.data.success) {
          this.zones = res.data.data.zones || [];
        }
      } catch (e) {
        console.error("Failed to load zones:", e);
      }
    },

    async loadWoredas() {
      if (!this.householdForm.zoneId) {
        this.woredas = [];
        this.householdForm.woredaId = null;
        return;
      }

      try {
        const res = await executeJson({
          function: "getWoredas",
          zoneId: this.householdForm.zoneId
        }, authUrl);

        if (res.data.success) {
          this.woredas = res.data.data.woredas || [];
        }
      } catch (e) {
        console.error("Failed to load woredas:", e);
      }
    },

    async loadKebeles() {
      if (!this.householdForm.woredaId) {
        this.kebeles = [];
        this.householdForm.kebeleId = null;
        return;
      }

      try {
        const res = await executeJson({
          function: "getKebeles",
          woredaId: this.householdForm.woredaId
        }, authUrl);

        if (res.data.success) {
          this.kebeles = res.data.data.kebeles || [];
        }
      } catch (e) {
        console.error("Failed to load kebeles:", e);
      }
    },

    async preselectLocation(kebeleId) {
      // This would load the hierarchy to pre-select the HEW's location
      // Implementation depends on your API structure
    },

    canProceed() {
      switch (this.step) {
        case 1:
          return this.householdForm.houseType;
        case 2:
          return this.householdForm.regionId &&
              this.householdForm.zoneId &&
              this.householdForm.woredaId &&
              this.householdForm.kebeleId;
        case 3:
          return this.householdForm.waterSource &&
              this.householdForm.latrineType &&
              this.householdForm.wasteDisposal;
        default:
          return true;
      }
    },

    nextStep() {
      if (this.canProceed()) {
        this.step++;
      }
    },

    previousStep() {
      if (this.step > 1) {
        this.step--;
      }
    },

    async getGPSLocation() {
      if (!navigator.geolocation) {
        this.gpsError = "Geolocation is not supported by your browser";
        return;
      }

      this.gpsLoading = true;
      this.gpsError = null;

      navigator.geolocation.getCurrentPosition(
          (position) => {
            this.householdForm.gpsLatitude = position.coords.latitude.toFixed(6);
            this.householdForm.gpsLongitude = position.coords.longitude.toFixed(6);
            this.gpsLoading = false;
          },
          (error) => {
            this.gpsLoading = false;
            switch(error.code) {
              case error.PERMISSION_DENIED:
                this.gpsError = "Location permission denied";
                break;
              case error.POSITION_UNAVAILABLE:
                this.gpsError = "Location information unavailable";
                break;
              case error.TIMEOUT:
                this.gpsError = "Location request timed out";
                break;
              default:
                this.gpsError = "An unknown error occurred";
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
      );
    },

    retryGPS() {
      this.gpsError = null;
      this.getGPSLocation();
    },

    getDisplayValue(field, value) {
      return this.displayValues[field]?.[value] || value || 'Not specified';
    },

    getSpecialCategories() {
      const categories = [];
      if (this.householdForm.isFemaleHeaded) categories.push('Female Headed');
      if (this.householdForm.isVulnerable) categories.push('Vulnerable');
      if (this.householdForm.hasElderlyMembers) categories.push('Has Elderly');
      if (this.householdForm.hasDisabledMembers) categories.push('Has Disabled');
      return categories;
    },

    getLocationSummary() {
      const parts = [];

      const kebele = this.kebeles.find(k => k.kebele_id == this.householdForm.kebeleId);
      if (kebele) parts.push(kebele.kebele_name);

      const woreda = this.woredas.find(w => w.woreda_id == this.householdForm.woredaId);
      if (woreda) parts.push(woreda.woreda_name);

      const zone = this.zones.find(z => z.zone_id == this.householdForm.zoneId);
      if (zone) parts.push(zone.zone_name);

      const region = this.regions.find(r => r.region_id == this.householdForm.regionId);
      if (region) parts.push(region.region_name);

      return parts.join(', ');
    },

    getModelCriteriaCount() {
      let count = 0;

      // Protected water source
      if (['piped_home', 'piped_yard', 'public_tap', 'protected_well', 'protected_spring']
          .includes(this.householdForm.waterSource)) {
        count++;
      }

      // Improved latrine
      if (['pit_with_slab', 'vip', 'flush_septic', 'flush_sewer', 'composting']
          .includes(this.householdForm.latrineType)) {
        count++;
      }

      // Handwashing facility with soap
      if (['water_soap', 'water_ash'].includes(this.householdForm.handwashingFacility)) {
        count++;
      }

      // Separate kitchen
      if (['separate_building', 'separate_room'].includes(this.householdForm.kitchenLocation)) {
        count++;
      }

      // Proper waste disposal
      if (['pit', 'composting', 'collection'].includes(this.householdForm.wasteDisposal)) {
        count++;
      }

      return count;
    },

    isModelHouseholdEligible() {
      return this.getModelCriteriaCount() >= 4;
    },

    async registerHousehold() {
      this.submitting = true;

      const data = {
        "function": "registerHousehold",
        ...this.householdForm
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('registered', {
            householdId: res.data.data.householdId,
            householdNumber: res.data.data.householdNumber
          });
        } else {
          alert(res.data.message || 'Failed to register household');
        }
      } catch (e) {
        console.error("Failed to register household:", e);
        alert('Failed to register household. Please try again.');
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.modal-dialog-scrollable .modal-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.progress {
  background-color: #e9ecef;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.bx-spin {
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

dl.row dt {
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
}

dl.row dd {
  margin-bottom: 0.5rem;
  font-weight: 500;
}
</style>